import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import api from '../config/api'
import toast from 'react-hot-toast'
import { 
  ChartBarIcon, 
  ShoppingBagIcon, 
  ChatBubbleLeftRightIcon, 
  ArrowLeftOnRectangleIcon,
  HomeIcon,
  ClipboardDocumentListIcon,
  ArrowPathIcon,
  EyeIcon,
  PrinterIcon,
  XMarkIcon,
  SparklesIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline'

const AdminDashboard = () => {
  const [stats, setStats] = useState(null)
  const [orders, setOrders] = useState([])
  const [quotes, setQuotes] = useState([])
  const [contacts, setContacts] = useState([])
  const [inquiries, setInquiries] = useState([])
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [isAuthorized, setIsAuthorized] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [selectedQuote, setSelectedQuote] = useState(null)
  
  // Product & Option CRUD State
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [showProductModal, setShowProductModal] = useState(false)
  const [productForm, setProductForm] = useState({
    id: '',
    product_id: '',
    slug: '',
    name: '',
    full_name: '',
    category: 'adult',
    price_per_box: 169.00,
    image: '',
    images: [],
    bristle_type: '',
    bristle_color: '',
    badge: '144 Units / Box',
    short_description: '',
    description: '',
    features: [],
    packaging: { unitsPerBox: 144, packagingType: 'Individually boxed', bulkAvailable: true },
    delivery_timeline: '3–4 weeks once order is confirmed',
    in_stock: 1
  })
  const [newFeatureText, setNewFeatureText] = useState('')
  const [newImageText, setNewImageText] = useState('')
  const [showOptionForm, setShowOptionForm] = useState(false)
  const [optionForm, setOptionForm] = useState({ id: '', name: '', value: '', price_modifier: 0.00 })
  
  // Real-time search and filters
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [deleteConfirmId, setDeleteConfirmId] = useState(null) // Pattern: 'type-id' (e.g. 'order-12')

  const [uploadingImage, setUploadingImage] = useState(false)
  const [uploadingGallery, setUploadingGallery] = useState(false)

  const handleImageUpload = async (file, isGallery = false) => {
    if (!file) return
    const formData = new FormData()
    formData.append('image', file)

    if (isGallery) {
      setUploadingGallery(true)
    } else {
      setUploadingImage(true)
    }

    try {
      const config = {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'multipart/form-data'
        }
      }
      const response = await api.post('/admin_api.php?action=upload_image', formData, config)
      if (response.data.success) {
        toast.success('Image uploaded successfully')
        if (isGallery) {
          setProductForm(prev => ({
            ...prev,
            images: [...prev.images, response.data.image_url]
          }))
        } else {
          setProductForm(prev => ({
            ...prev,
            image: response.data.image_url
          }))
        }
      } else {
        toast.error(response.data.error || 'Failed to upload image')
      }
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Image upload failed')
    } finally {
      if (isGallery) {
        setUploadingGallery(false)
      } else {
        setUploadingImage(false)
      }
    }
  }

  const navigate = useNavigate()
  const location = useLocation()

  // Derive activeTab from URL
  const pathParts = location.pathname.split('/')
  const lastPart = pathParts[pathParts.length - 1]
  const activeTab = lastPart === 'dashboard' ? 'overview' : lastPart

  // Reset search when switching tabs
  useEffect(() => {
    setSearchTerm('')
    setDeleteConfirmId(null)
  }, [activeTab])

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('adminToken')
      if (!token) {
        navigate('/admin/login')
        return false
      }
      setIsAuthorized(true)
      return true
    }

    if (checkAuth()) {
      fetchData()
    }
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      const [statsRes, ordersRes, quotesRes, contactsRes, inquiriesRes, productsRes] = await Promise.all([
        api.get('/admin_api.php?action=dashboard_stats', config),
        api.get('/admin_api.php?action=get_orders', config),
        api.get('/admin_api.php?action=get_quotes', config),
        api.get('/admin_api.php?action=get_contacts', config),
        api.get('/admin_api.php?action=get_custom_inquiries', config),
        api.get('/admin_api.php?action=get_products')
      ])
      setStats(statsRes.data.stats)
      setOrders(ordersRes.data.data || [])
      setQuotes(quotesRes.data.data || [])
      setContacts(contactsRes.data.data || [])
      setInquiries(inquiriesRes.data.data || [])
      setProducts(productsRes.data.data || [])
    } catch (error) {
      toast.error('Failed to load dashboard data')
      if (error.message && error.message.includes('401')) {
        localStorage.removeItem('adminToken')
        navigate('/admin/login')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      const response = await api.post('/admin_api.php?action=update_order_status', {
        id: orderId,
        status: newStatus
      }, config)

      if (response.data.success) {
        toast.success(`Order #${orderId} updated to ${newStatus}`)
        // Update local state
        setOrders(orders.map(o => o.id === orderId ? { ...o, status: newStatus } : o))
        if (selectedOrder && selectedOrder.id === orderId) {
          setSelectedOrder({ ...selectedOrder, status: newStatus })
        }
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update status')
    }
  }

  // Deletion logic
  const handleDeleteOrder = async (id) => {
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      const response = await api.post('/admin_api.php?action=delete_order', { id }, config)
      if (response.data.success) {
        toast.success('Order deleted successfully')
        setOrders(orders.filter(o => o.id !== id))
        setDeleteConfirmId(null)
        if (selectedOrder && selectedOrder.id === id) {
          setSelectedOrder(null)
        }
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete order')
    }
  }

  const handleDeleteQuote = async (id) => {
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      const response = await api.post('/admin_api.php?action=delete_quote', { id }, config)
      if (response.data.success) {
        toast.success('Bulk quote request deleted')
        setQuotes(quotes.filter(q => q.id !== id))
        setDeleteConfirmId(null)
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete quote')
    }
  }

  const handleDeleteContact = async (id) => {
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      const response = await api.post('/admin_api.php?action=delete_contact', { id }, config)
      if (response.data.success) {
        toast.success('Message deleted')
        setContacts(contacts.filter(c => c.id !== id))
        setDeleteConfirmId(null)
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete message')
    }
  }

  const handleDeleteInquiry = async (id) => {
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      const response = await api.post('/admin_api.php?action=delete_inquiry', { id }, config)
      if (response.data.success) {
        toast.success('Custom inquiry deleted')
        setInquiries(inquiries.filter(i => i.id !== id))
        setDeleteConfirmId(null)
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete custom inquiry')
    }
  }

  const handleOpenAddProduct = () => {
    setSelectedProduct(null)
    setProductForm({
      id: '',
      product_id: '',
      slug: '',
      name: '',
      full_name: '',
      category: 'adult',
      price_per_box: 169.00,
      image: '',
      images: [],
      bristle_type: '',
      bristle_color: '',
      badge: '144 Units / Box',
      short_description: '',
      description: '',
      features: [],
      packaging: { unitsPerBox: 144, packagingType: 'Individually boxed', bulkAvailable: true },
      delivery_timeline: '3–4 weeks once order is confirmed',
      in_stock: 1
    })
    setShowProductModal(true)
  }

  const handleOpenEditProduct = (prod) => {
    setSelectedProduct(prod)
    setProductForm({
      id: prod.id_db,
      product_id: prod.product_id || prod.id,
      slug: prod.slug,
      name: prod.name,
      full_name: prod.fullName || prod.full_name,
      category: prod.category,
      price_per_box: prod.pricePerBox || prod.price_per_box,
      image: prod.image,
      images: prod.images || [],
      bristle_type: prod.bristleType || prod.bristle_type,
      bristle_color: prod.bristleColor || prod.bristle_color,
      badge: prod.badge,
      short_description: prod.shortDescription || prod.short_description,
      description: prod.description,
      features: prod.features || [],
      packaging: prod.packaging || { unitsPerBox: 144, packagingType: 'Individually boxed', bulkAvailable: true },
      delivery_timeline: prod.deliveryTimeline || prod.delivery_timeline,
      in_stock: prod.in_stock ? 1 : 0
    })
    setShowProductModal(true)
  }

  const handleSaveProduct = async (e) => {
    e.preventDefault()
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      let response
      if (selectedProduct) {
        response = await api.post('/admin_api.php?action=update_product', productForm, config)
      } else {
        response = await api.post('/admin_api.php?action=add_product', productForm, config)
      }

      if (response.data.success) {
        toast.success(selectedProduct ? 'Product updated successfully' : 'Product added successfully')
        setShowProductModal(false)
        fetchData()
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save product')
    }
  }

  const handleDeleteProduct = async (id) => {
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      const response = await api.post('/admin_api.php?action=delete_product', { id }, config)
      if (response.data.success) {
        toast.success('Product deleted')
        setProducts(products.filter(p => p.id_db !== id))
        setDeleteConfirmId(null)
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete product')
    }
  }

  const handleAddOption = async (e) => {
    e.preventDefault()
    if (!selectedProduct) return
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      const payload = {
        product_id: selectedProduct.product_id || selectedProduct.id,
        name: optionForm.name,
        value: optionForm.value,
        price_modifier: optionForm.price_modifier
      }
      
      const response = await api.post('/admin_api.php?action=add_product_option', payload, config)
      if (response.data.success) {
        toast.success('Option added successfully')
        setOptionForm({ id: '', name: '', value: '', price_modifier: 0.00 })
        setShowOptionForm(false)
        
        // Refresh product in-place to update modal view
        const pRes = await api.get('/admin_api.php?action=get_products')
        const updatedProds = pRes.data.data || []
        setProducts(updatedProds)
        const currentProd = updatedProds.find(p => p.id_db === selectedProduct.id_db)
        if (currentProd) setSelectedProduct(currentProd)
      }
    } catch (error) {
      toast.error(error.message || 'Failed to add option')
    }
  }

  const handleDeleteOption = async (optionId) => {
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      const response = await api.post('/admin_api.php?action=delete_product_option', { id: optionId }, config)
      if (response.data.success) {
        toast.success('Option deleted')
        
        // Refresh product in-place
        const pRes = await api.get('/admin_api.php?action=get_products')
        const updatedProds = pRes.data.data || []
        setProducts(updatedProds)
        const currentProd = updatedProds.find(p => p.id_db === selectedProduct.id_db)
        if (currentProd) setSelectedProduct(currentProd)
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete option')
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('adminToken')
    localStorage.removeItem('adminUser')
    navigate('/admin/login')
  }

  if (!isAuthorized) return null

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white">
        <ArrowPathIcon className="w-16 h-16 text-emerald-500 animate-spin mb-4" />
        <p className="text-slate-400 text-lg font-semibold tracking-wide">Loading Premium Twiggle Dashboard...</p>
      </div>
    )
  }

  const hasCustomEngraving = (order) => {
    try {
      const items = typeof order.cart_items === 'string'
        ? JSON.parse(order.cart_items)
        : order.cart_items;
      return Array.isArray(items) && items.some(item => item.engravingImage);
    } catch (_) {
      return false;
    }
  }

  // Filtering calculations
  const filteredOrders = orders.filter(order => {
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter
    const matchesSearch = searchTerm === '' || 
      `${order.first_name} ${order.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `ord-${order.id}`.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesStatus && matchesSearch
  })

  const filteredQuotes = quotes.filter(quote => {
    return searchTerm === '' ||
      quote.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.organization.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (quote.message && quote.message.toLowerCase().includes(searchTerm.toLowerCase()))
  })

  const filteredContacts = contacts.filter(contact => {
    return searchTerm === '' ||
      `${contact.first_name} ${contact.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.message.toLowerCase().includes(searchTerm.toLowerCase())
  })

  const filteredInquiries = inquiries.filter(inquiry => {
    return searchTerm === '' ||
      inquiry.clinic_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inquiry.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inquiry.requirements.toLowerCase().includes(searchTerm.toLowerCase())
  })

  return (
    <div className="min-h-screen bg-slate-950 flex font-sans text-slate-100 selection:bg-emerald-500 selection:text-white">
      {/* Sidebar */}
      <aside 
        className="w-72 bg-slate-900/60 backdrop-blur-md text-white flex flex-col fixed inset-y-0 border-r border-slate-800 shadow-2xl z-20"
        style={{ width: '18rem' }}
      >
        <div className="p-8 border-b border-slate-800/80">
          <Link to="/" className="group flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center font-black text-slate-950 shadow-lg shadow-emerald-500/20 group-hover:scale-105 transition-transform duration-300">
              T
            </div>
            <h1 className="text-xl font-black tracking-tight leading-none">
              TWIGGLE <span className="text-emerald-400 font-medium text-xs block tracking-widest mt-0.5">ADMIN SUITE</span>
            </h1>
          </Link>
        </div>
        
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          <NavItem to="/admin/dashboard" active={activeTab === 'overview'} icon={<ChartBarIcon className="w-5 h-5"/>} label="Overview" />
          <NavItem to="/admin/dashboard/products" active={activeTab === 'products'} icon={<ShoppingBagIcon className="w-5 h-5"/>} label="Products" badge={products.length} badgeColor="bg-emerald-500 text-slate-950" />
          <NavItem to="/admin/dashboard/orders" active={activeTab === 'orders'} icon={<ShoppingBagIcon className="w-5 h-5"/>} label="Orders" badge={orders.length > 0 ? orders.filter(o => o.status === 'pending').length : 0} badgeColor="bg-amber-500 text-slate-950" />
          <NavItem to="/admin/dashboard/quotes" active={activeTab === 'quotes'} icon={<ClipboardDocumentListIcon className="w-5 h-5"/>} label="Bulk Quotes" badge={quotes.length} />
          <NavItem to="/admin/dashboard/inquiries" active={activeTab === 'inquiries'} icon={<SparklesIcon className="w-5 h-5"/>} label="Custom Inquiries" badge={inquiries.length} badgeColor="bg-indigo-500 text-white" />
          <NavItem to="/admin/dashboard/contacts" active={activeTab === 'contacts'} icon={<ChatBubbleLeftRightIcon className="w-5 h-5"/>} label="Messages" badge={contacts.length} />
        </nav>
 
        <div className="p-4 border-t border-slate-800/80 bg-slate-950/40">
          <div className="flex items-center gap-3 px-3 py-2 mb-4 bg-slate-850 rounded-xl border border-slate-800/55">
            <div className="w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center font-black text-emerald-400 shadow-inner">
              AD
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-slate-200 truncate">Administrator</p>
              <p className="text-[10px] text-slate-500 truncate">admin@twiggle.ca</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-3 w-full p-3 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all duration-300 font-medium">
            <ArrowLeftOnRectangleIcon className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>
 
      {/* Main Content */}
      <main 
        className="flex-1 ml-72 overflow-auto min-h-screen flex flex-col bg-slate-950"
        style={{ marginLeft: '18rem' }}
      >
        <header className="flex justify-between items-center px-10 py-6 border-b border-slate-900 bg-slate-950/80 sticky top-0 backdrop-blur z-10">
          <div>
            <h2 className="text-2xl font-black text-white tracking-tight">
              {activeTab === 'overview' ? 'Dashboard Overview' : 
               activeTab === 'inquiries' ? 'Custom Clinic Inquiries' :
               activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Welcome back. Manage your digital store and business inquiries.</p>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={fetchData} className="p-2 bg-slate-900 border border-slate-800 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-all cursor-pointer" title="Refresh Live Data">
              <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <Link to="/" className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-slate-950 rounded-xl text-sm font-bold hover:bg-emerald-400 hover:shadow-lg hover:shadow-emerald-500/20 transition-all">
              <HomeIcon className="w-4 h-4" />
              Go to Site
            </Link>
          </div>
        </header>

        <div className="flex-1 p-10 max-w-7xl w-full mx-auto space-y-10">
          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && stats && (
            <div className="space-y-10 animate-in fade-in duration-500">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                <StatCard 
                  label="Total Revenue" 
                  value={`$${stats.revenue.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`} 
                  color="from-emerald-500/20 to-emerald-500/5 text-emerald-400 border-emerald-500/25"
                  icon={<ChartBarIcon className="w-6 h-6 text-emerald-400" />} 
                />
                <StatCard 
                  label="Active Orders" 
                  value={stats.orders} 
                  color="from-blue-500/20 to-blue-500/5 text-blue-400 border-blue-500/25"
                  icon={<ShoppingBagIcon className="w-6 h-6 text-blue-400" />} 
                />
                <StatCard 
                  label="Bulk Quotes" 
                  value={stats.quotes} 
                  color="from-amber-500/20 to-amber-500/5 text-amber-400 border-amber-500/25"
                  icon={<ClipboardDocumentListIcon className="w-6 h-6 text-amber-400" />} 
                />
                <StatCard 
                  label="Custom Inquiries" 
                  value={stats.inquiries} 
                  color="from-indigo-500/20 to-indigo-500/5 text-indigo-400 border-indigo-500/25"
                  icon={<SparklesIcon className="w-6 h-6 text-indigo-400" />} 
                />
                <StatCard 
                  label="Messages" 
                  value={stats.contacts} 
                  color="from-purple-500/20 to-purple-500/5 text-purple-400 border-purple-500/25"
                  icon={<ChatBubbleLeftRightIcon className="w-6 h-6 text-purple-400" />} 
                />
              </div>

              {/* Split Dashboard Content */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Recent Orders (Left side) */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl lg:col-span-7">
                  <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-800">
                    <h3 className="font-bold text-lg text-white flex items-center gap-2">
                      <ShoppingBagIcon className="w-5 h-5 text-emerald-400" />
                      Recent Orders
                    </h3>
                    <Link to="/admin/dashboard/orders" className="text-xs text-emerald-400 font-bold hover:underline flex items-center gap-1 group">
                      View All <ArrowRightIcon className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-slate-300">
                      <thead>
                        <tr className="border-b border-slate-800 text-slate-500 text-xs font-semibold">
                          <th className="py-3 pr-4">Customer</th>
                          <th className="py-3 px-4">Status</th>
                          <th className="py-3 px-4 text-right">Total</th>
                          <th className="py-3 pl-4 text-right">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/60">
                        {orders.slice(0, 5).map(order => (
                          <tr key={order.id} className="hover:bg-slate-800/30 transition-colors">
                            <td className="py-3.5 pr-4">
                              <div className="font-bold text-white text-sm">{order.first_name} {order.last_name}</div>
                              <div className="text-xs text-slate-500">{order.email}</div>
                            </td>
                            <td className="py-3.5 px-4">
                              <span className={`px-2 py-0.5 text-[9px] font-black uppercase rounded-md tracking-wider ${
                                order.status === 'confirmed' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 
                                order.status === 'pending' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 
                                order.status === 'shipped' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 
                                order.status === 'delivered' ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20' :
                                'bg-slate-800 text-slate-400 border border-slate-700'
                              }`}>
                                {order.status}
                              </span>
                            </td>
                            <td className="py-3.5 px-4 font-bold text-white text-right">${parseFloat(order.total).toFixed(2)}</td>
                            <td className="py-3.5 pl-4 text-xs text-slate-500 text-right">
                              {new Date(order.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                        {orders.length === 0 && (
                          <tr>
                            <td colSpan="4" className="py-8 text-center text-slate-500">No orders logged.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Recent Activity: Quotes & Inquiries (Right side) */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl lg:col-span-5 flex flex-col">
                  <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-800">
                    <h3 className="font-bold text-lg text-white flex items-center gap-2">
                      <SparklesIcon className="w-5 h-5 text-indigo-400" />
                      Recent Activity Feed
                    </h3>
                  </div>

                  <div className="space-y-4 overflow-y-auto max-h-[350px] pr-2 flex-1 scrollbar-thin">
                    {/* inquiries stream */}
                    {inquiries.slice(0, 3).map(inq => (
                      <div key={`inq-${inq.id}`} className="p-4 bg-slate-950 border border-slate-850 rounded-xl flex gap-3 relative hover:border-slate-800 transition-all group">
                        <div className="w-8 h-8 rounded-lg bg-indigo-500/10 text-indigo-400 border border-indigo-500/25 flex items-center justify-center shrink-0">
                          <SparklesIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start gap-2">
                            <h4 className="text-xs font-black text-indigo-400 uppercase tracking-widest leading-none">Custom Project</h4>
                            <span className="text-[10px] text-slate-500">{new Date(inq.created_at).toLocaleDateString()}</span>
                          </div>
                          <p className="text-sm font-bold text-white mt-1.5">{inq.clinic_name}</p>
                          <p className="text-xs text-slate-400 line-clamp-2 mt-1 italic">"{inq.requirements}"</p>
                        </div>
                      </div>
                    ))}

                    {/* quotes stream */}
                    {quotes.slice(0, 3).map(quote => (
                      <div key={`q-${quote.id}`} className="p-4 bg-slate-950 border border-slate-850 rounded-xl flex gap-3 hover:border-slate-800 transition-all group">
                        <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/25 flex items-center justify-center shrink-0">
                          <ClipboardDocumentListIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start gap-2">
                            <h4 className="text-xs font-black text-amber-400 uppercase tracking-widest leading-none">Bulk Quote</h4>
                            <span className="text-[10px] text-slate-500">{new Date(quote.created_at).toLocaleDateString()}</span>
                          </div>
                          <p className="text-sm font-bold text-white mt-1.5">{quote.organization}</p>
                          <p className="text-xs text-slate-400 mt-1 font-semibold">Quantity: <span className="text-emerald-400">{quote.quantity} units</span></p>
                        </div>
                      </div>
                    ))}

                    {inquiries.length === 0 && quotes.length === 0 && (
                      <div className="py-12 text-center text-slate-500 text-sm">
                        No recent inquiries or quotes.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ORDERS TAB */}
          {activeTab === 'orders' && (
            <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl animate-in slide-in-from-bottom-4 duration-500 space-y-6 p-6">
              {/* Header and Filter block */}
              <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-4 pb-6 border-b border-slate-800">
                <div className="relative flex-1 max-w-md">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-slate-500" />
                  </span>
                  <input
                    type="text"
                    placeholder="Search by ID, name, email, or city..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-11 pr-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm text-white placeholder-slate-500 transition-all outline-none"
                  />
                </div>
                {/* Status selector */}
                <div className="flex flex-wrap items-center gap-2">
                  {['all', 'pending', 'confirmed', 'shipped', 'delivered', 'cancelled'].map(status => (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
                        statusFilter === status 
                          ? 'bg-emerald-500 text-slate-950 shadow-lg shadow-emerald-500/15'
                          : 'bg-slate-950 hover:bg-slate-850 text-slate-400 hover:text-white border border-slate-850'
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>

              {/* Orders Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-slate-300">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-500 text-xs font-semibold">
                      <th className="py-4 px-4">Order ID</th>
                      <th className="py-4 px-4">Customer</th>
                      <th className="py-4 px-4 text-right">Total</th>
                      <th className="py-4 px-4 text-center">Status</th>
                      <th className="py-4 px-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60">
                    {filteredOrders.map(order => (
                      <tr key={order.id} className="hover:bg-slate-850/20 transition-all group">
                        <td className="py-4 px-4 font-mono text-xs font-black text-slate-500 group-hover:text-emerald-400 transition-colors">
                          #ORD-{order.id}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-white text-sm">{order.first_name} {order.last_name}</span>
                            {hasCustomEngraving(order) && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-black uppercase rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 tracking-wider">
                                ✨ Logo Engraving
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">{order.email}</div>
                        </td>
                        <td className="py-4 px-4 font-black text-white text-right">${parseFloat(order.total).toFixed(2)}</td>
                        <td className="py-4 px-4 text-center">
                          <select 
                            value={order.status}
                            onChange={(e) => handleUpdateStatus(order.id, e.target.value)}
                            className={`text-[10px] font-black uppercase tracking-wider rounded-lg border border-slate-800 py-1 pl-3 pr-8 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-slate-950 cursor-pointer transition-all ${
                              order.status === 'confirmed' ? 'text-emerald-400' : 
                              order.status === 'pending' ? 'text-amber-400' : 
                              order.status === 'shipped' ? 'text-blue-400' : 
                              order.status === 'delivered' ? 'text-indigo-400' :
                              order.status === 'cancelled' ? 'text-red-400' : 'text-slate-400'
                            }`}
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="shipped">Shipped</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-center gap-1">
                            <button 
                              onClick={() => setSelectedOrder(order)}
                              className="p-2 text-slate-400 hover:text-emerald-400 hover:bg-slate-800 rounded-lg transition-all"
                              title="View Order Invoice"
                            >
                              <EyeIcon className="w-5 h-5" />
                            </button>
                            <button 
                              onClick={() => {
                                setSelectedOrder(order);
                                setTimeout(() => window.print(), 500);
                              }}
                              className="p-2 text-slate-400 hover:text-indigo-400 hover:bg-slate-800 rounded-lg transition-all"
                              title="Print Invoice"
                            >
                              <PrinterIcon className="w-5 h-5" />
                            </button>
                            
                            {/* Inline Double Confirmation Delete */}
                            {deleteConfirmId === `order-${order.id}` ? (
                              <div className="flex items-center gap-1.5 ml-2">
                                <button 
                                  onClick={() => handleDeleteOrder(order.id)}
                                  className="px-2 py-1 bg-red-600 hover:bg-red-700 text-slate-100 text-[10px] font-black uppercase rounded-md tracking-wider transition-all"
                                >
                                  Delete
                                </button>
                                <button 
                                  onClick={() => setDeleteConfirmId(null)}
                                  className="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-400 text-[10px] font-black uppercase rounded-md tracking-wider transition-all"
                                >
                                  X
                                </button>
                              </div>
                            ) : (
                              <button 
                                onClick={() => setDeleteConfirmId(`order-${order.id}`)}
                                className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-all"
                                title="Delete Order"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredOrders.length === 0 && (
                      <tr>
                        <td colSpan="5" className="py-12 text-center text-slate-500">No orders found matching criteria.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* BULK QUOTES TAB */}
          {activeTab === 'quotes' && (
            <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl animate-in slide-in-from-bottom-4 duration-500 space-y-6 p-6">
              {/* Header filter */}
              <div className="flex justify-between items-center pb-6 border-b border-slate-800">
                <div className="relative flex-1 max-w-md">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-slate-500" />
                  </span>
                  <input
                    type="text"
                    placeholder="Search by requester, organization, email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-11 pr-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm text-white placeholder-slate-500 transition-all outline-none"
                  />
                </div>
              </div>

              {/* Quotes Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-slate-300">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-500 text-xs font-semibold">
                      <th className="py-4 px-4">Requester</th>
                      <th className="py-4 px-4">Organization</th>
                      <th className="py-4 px-4">Quantity</th>
                      <th className="py-4 px-4">Date</th>
                      <th className="py-4 px-4">Message</th>
                      <th className="py-4 px-4 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60">
                    {filteredQuotes.map(quote => (
                      <tr key={quote.id} className="hover:bg-slate-850/20 transition-all">
                        <td className="py-4 px-4">
                          <div className="font-bold text-white text-sm">{quote.name}</div>
                          <div className="text-xs text-slate-500">{quote.email}</div>
                        </td>
                        <td className="py-4 px-4 text-slate-300 font-medium">{quote.organization}</td>
                        <td className="py-4 px-4 font-black text-emerald-400">{quote.quantity} units</td>
                        <td className="py-4 px-4 text-slate-500 text-xs">{new Date(quote.created_at).toLocaleDateString()}</td>
                        <td className="py-4 px-4 text-slate-400 text-xs max-w-xs truncate" title={quote.message}>{quote.message || '—'}</td>
                        <td className="py-4 px-4 text-center">
                          {deleteConfirmId === `quote-${quote.id}` ? (
                            <div className="flex items-center justify-center gap-1.5">
                              <button 
                                onClick={() => handleDeleteQuote(quote.id)}
                                className="px-2 py-1 bg-red-600 hover:bg-red-700 text-slate-100 text-[10px] font-black uppercase rounded-md tracking-wider transition-all"
                              >
                                Delete
                              </button>
                              <button 
                                onClick={() => setDeleteConfirmId(null)}
                                className="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-400 text-[10px] font-black uppercase rounded-md tracking-wider transition-all"
                              >
                                X
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-1">
                              <button 
                                onClick={() => setSelectedQuote(quote)}
                                className="p-2 text-slate-400 hover:text-emerald-400 hover:bg-slate-800 rounded-lg transition-all"
                                title="View Quote Request"
                              >
                                <EyeIcon className="w-5 h-5" />
                              </button>
                              <button 
                                onClick={() => setDeleteConfirmId(`quote-${quote.id}`)}
                                className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-850 rounded-lg transition-all"
                                title="Delete Quote Request"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {filteredQuotes.length === 0 && (
                      <tr>
                        <td colSpan="6" className="py-12 text-center text-slate-500">No quote requests found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* CUSTOM INQUIRIES TAB (Clinic-branding requests) */}
          {activeTab === 'inquiries' && (
            <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
              {/* Header filter */}
              <div className="flex justify-between items-center pb-6 border-b border-slate-800">
                <div className="relative flex-1 max-w-md">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-slate-500" />
                  </span>
                  <input
                    type="text"
                    placeholder="Search by clinic name, email, requirements..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-11 pr-4 py-2.5 bg-slate-900 border border-slate-800 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm text-white placeholder-slate-500 transition-all outline-none"
                  />
                </div>
              </div>

              {/* Clinic Inquiry Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredInquiries.map(inq => (
                  <div key={inq.id} className="bg-slate-900 border border-slate-800 hover:border-slate-700/60 shadow-xl rounded-2xl p-6 flex flex-col relative transition-all group overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-full blur-xl pointer-events-none" />
                    
                    <div className="flex justify-between items-start gap-4 pb-4 border-b border-slate-800/80">
                      <div>
                        <h4 className="text-lg font-black text-white">{inq.clinic_name}</h4>
                        <a href={`mailto:${inq.email}`} className="text-xs text-indigo-400 font-semibold hover:underline block mt-0.5">{inq.email}</a>
                      </div>
                      <span className="text-[10px] text-slate-500 font-bold bg-slate-950 px-2.5 py-1 rounded-md border border-slate-850">
                        {new Date(inq.created_at).toLocaleDateString()}
                      </span>
                    </div>

                    <div className="mt-4 flex-1">
                      <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Branding Requirements:</h5>
                      <p className="text-sm text-slate-300 bg-slate-950 p-4 rounded-xl border border-slate-850/60 leading-relaxed italic select-all">
                        "{inq.requirements}"
                      </p>
                    </div>

                    <div className="mt-6 flex justify-between items-center pt-4 border-t border-slate-800/50">
                      <a href={`mailto:${inq.email}?subject=Twiggle Custom Project inquiry - ${encodeURIComponent(inq.clinic_name)}`} className="text-xs font-black text-indigo-400 group-hover:text-indigo-300 flex items-center gap-1 hover:underline cursor-pointer">
                        Reply via Email <EnvelopeIcon className="w-4 h-4 inline" />
                      </a>

                      {deleteConfirmId === `inquiry-${inq.id}` ? (
                        <div className="flex items-center gap-1.5">
                          <button 
                            onClick={() => handleDeleteInquiry(inq.id)}
                            className="px-2.5 py-1.5 bg-red-600 hover:bg-red-700 text-slate-100 text-[10px] font-black uppercase rounded-md tracking-wider transition-all"
                          >
                            Delete
                          </button>
                          <button 
                            onClick={() => setDeleteConfirmId(null)}
                            className="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-400 text-[10px] font-black uppercase rounded-md tracking-wider transition-all"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button 
                          onClick={() => setDeleteConfirmId(`inquiry-${inq.id}`)}
                          className="p-2 text-slate-500 hover:text-red-400 hover:bg-slate-850 rounded-lg transition-all duration-200"
                          title="Delete Custom Inquiry"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {filteredInquiries.length === 0 && (
                  <div className="col-span-full py-16 text-center text-slate-500">
                    No custom clinic inquiries found.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* CONTACTS / MESSAGES TAB */}
          {activeTab === 'contacts' && (
            <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
              {/* Header filter */}
              <div className="flex justify-between items-center pb-6 border-b border-slate-800">
                <div className="relative flex-1 max-w-md">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-slate-500" />
                  </span>
                  <input
                    type="text"
                    placeholder="Search by sender, subject, message content..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-11 pr-4 py-2.5 bg-slate-900 border border-slate-800 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm text-white placeholder-slate-500 transition-all outline-none"
                  />
                </div>
              </div>

              {/* Message Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredContacts.map(contact => (
                  <div key={contact.id} className="bg-slate-900 border border-slate-800 hover:border-slate-750 shadow-xl rounded-2xl p-6 flex flex-col relative transition-all group overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 rounded-full blur-xl pointer-events-none" />
                    
                    <div className="flex justify-between items-start gap-4 pb-4 border-b border-slate-800/80">
                      <div>
                        <h4 className="text-lg font-black text-white">{contact.first_name} {contact.last_name}</h4>
                        <a href={`mailto:${contact.email}`} className="text-xs text-purple-400 font-semibold hover:underline block mt-0.5">{contact.email}</a>
                      </div>
                      <span className="text-[10px] text-slate-500 font-bold bg-slate-950 px-2.5 py-1 rounded-md border border-slate-850">
                        {new Date(contact.created_at).toLocaleDateString()}
                      </span>
                    </div>

                    <div className="mt-4 flex-1">
                      <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Subject: <span className="text-slate-200 normal-case ml-1 font-bold">{contact.subject}</span></h5>
                      <p className="text-sm text-slate-300 bg-slate-950 p-4 rounded-xl border border-slate-850/60 leading-relaxed italic select-all">
                        "{contact.message}"
                      </p>
                    </div>

                    <div className="mt-6 flex justify-between items-center pt-4 border-t border-slate-800/50">
                      <a href={`mailto:${contact.email}?subject=Re: Twiggle Contact Message - ${encodeURIComponent(contact.subject)}`} className="text-xs font-black text-purple-400 group-hover:text-purple-300 flex items-center gap-1 hover:underline cursor-pointer">
                        Reply via Email <EnvelopeIcon className="w-4 h-4 inline" />
                      </a>

                      {deleteConfirmId === `contact-${contact.id}` ? (
                        <div className="flex items-center gap-1.5">
                          <button 
                            onClick={() => handleDeleteContact(contact.id)}
                            className="px-2.5 py-1.5 bg-red-600 hover:bg-red-700 text-slate-100 text-[10px] font-black uppercase rounded-md tracking-wider transition-all"
                          >
                            Delete
                          </button>
                          <button 
                            onClick={() => setDeleteConfirmId(null)}
                            className="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-400 text-[10px] font-black uppercase rounded-md tracking-wider transition-all"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button 
                          onClick={() => setDeleteConfirmId(`contact-${contact.id}`)}
                          className="p-2 text-slate-500 hover:text-red-400 hover:bg-slate-850 rounded-lg transition-all duration-200"
                          title="Delete Message"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {filteredContacts.length === 0 && (
                  <div className="col-span-full py-16 text-center text-slate-500">
                    No contact messages found.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* PRODUCTS TAB */}
          {activeTab === 'products' && (
            <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
              <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 pb-6 border-b border-slate-800">
                <div className="relative flex-1 max-w-md">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-slate-500" />
                  </span>
                  <input
                    type="text"
                    placeholder="Search by product name, category..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-11 pr-4 py-2.5 bg-slate-900 border border-slate-800 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm text-white placeholder-slate-500 transition-all outline-none"
                  />
                </div>
                <button
                  onClick={handleOpenAddProduct}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-500 text-slate-950 rounded-xl text-sm font-black hover:bg-emerald-400 hover:shadow-lg hover:shadow-emerald-500/20 transition-all cursor-pointer"
                >
                  + Add Product
                </button>
              </div>

              {/* Products Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {products
                  .filter(p => searchTerm === '' || p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.category.toLowerCase().includes(searchTerm.toLowerCase()))
                  .map(prod => (
                    <div key={prod.id_db || prod.id} className="bg-slate-900 border border-slate-800 hover:border-slate-700/60 shadow-xl rounded-2xl p-6 flex flex-col justify-between relative transition-all group overflow-hidden">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-xl pointer-events-none" />
                      
                      <div>
                        {/* Header */}
                        <div className="flex justify-between items-start gap-4 pb-4 border-b border-slate-800/80">
                          <div className="min-w-0">
                            <h4 className="text-lg font-black text-white truncate">{prod.name}</h4>
                            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">{prod.category} bamboo brush</p>
                          </div>
                          <span className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-md border ${
                            prod.inStock || prod.in_stock
                              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                              : 'bg-red-500/10 text-red-400 border-red-500/20'
                          }`}>
                            {prod.inStock || prod.in_stock ? 'In Stock' : 'Out of Stock'}
                          </span>
                        </div>

                        {/* Image Preview & Details */}
                        <div className="mt-4 flex gap-4">
                          <div className="w-20 h-20 bg-slate-950 border border-slate-855 rounded-xl flex items-center justify-center shrink-0 p-2">
                            <img src={prod.image} alt={prod.name} className="max-h-full max-w-full object-contain" />
                          </div>
                          <div className="text-xs space-y-1 py-1">
                            <p className="text-slate-400 font-semibold">Price per Box: <span className="text-emerald-400 font-extrabold">${parseFloat(prod.pricePerBox || prod.price_per_box).toFixed(2)}</span></p>
                            <p className="text-slate-400">Badge: <span className="text-slate-200">{prod.badge}</span></p>
                            <p className="text-slate-400 truncate max-w-[150px]" title={prod.fullName || prod.full_name}>Full Name: <span className="text-slate-300 font-medium">{prod.fullName || prod.full_name}</span></p>
                          </div>
                        </div>

                        {/* Options badge list */}
                        {prod.options && prod.options.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-slate-800/50">
                            <h5 className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-2">Configured Options ({prod.options.length}):</h5>
                            <div className="flex flex-wrap gap-1.5">
                              {prod.options.map(opt => (
                                <span key={opt.id} className="text-[10px] px-2 py-0.5 bg-slate-950 border border-slate-800 text-slate-400 rounded-md font-semibold">
                                  {opt.name}: {opt.value} {opt.price_modifier > 0 && `(+$${opt.price_modifier})`}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="mt-6 flex justify-between items-center pt-4 border-t border-slate-800/50">
                        <button
                          onClick={() => handleOpenEditProduct(prod)}
                          className="text-xs font-black text-emerald-400 hover:text-emerald-300 flex items-center gap-1 cursor-pointer"
                        >
                          Edit & Options
                        </button>

                        {deleteConfirmId === `product-${prod.id_db}` ? (
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => handleDeleteProduct(prod.id_db)}
                              className="px-2.5 py-1.5 bg-red-600 hover:bg-red-700 text-slate-100 text-[10px] font-black uppercase rounded-md tracking-wider transition-all"
                            >
                              Confirm Delete
                            </button>
                            <button
                              onClick={() => setDeleteConfirmId(null)}
                              className="px-2.5 py-1.5 bg-slate-850 hover:bg-slate-800 text-slate-400 text-[10px] font-black uppercase rounded-md tracking-wider transition-all"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirmId(`product-${prod.id_db}`)}
                            className="p-2 text-slate-500 hover:text-red-400 hover:bg-slate-850 rounded-lg transition-all duration-200"
                            title="Delete Product"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                {products.length === 0 && (
                  <div className="col-span-full py-16 text-center text-slate-500">
                    No products configured. Click Add Product to get started.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Order Details Invoice Modal */}
      {selectedOrder && (
        <div id="invoice-modal-wrapper" className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 md:p-10 bg-slate-950/80 backdrop-blur-md print:p-0 print:bg-white print:static print:block">
          <div id="invoice-modal-container" className="bg-slate-900 border border-slate-800 w-full max-w-3xl max-h-[90vh] overflow-auto rounded-2xl shadow-2xl animate-in zoom-in-95 duration-300 print:max-h-none print:shadow-none print:rounded-none print:w-full print:m-0 print:border-0 print:bg-white">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-800 flex justify-between items-center print:hidden">
              <div>
                <h3 className="text-lg font-black text-white">Order Details</h3>
                <p className="text-xs text-slate-500">Order Ref: #ORD-{selectedOrder.id}</p>
              </div>
              <div className="flex items-center gap-3">
                {/* Live update status from inside invoice */}
                <select 
                  value={selectedOrder.status}
                  onChange={(e) => handleUpdateStatus(selectedOrder.id, e.target.value)}
                  className="text-[10px] font-black uppercase tracking-wider rounded-lg border border-slate-800 py-1.5 pl-3 pr-8 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-slate-950 text-white cursor-pointer transition-all"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <button 
                  onClick={() => window.print()}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-slate-950 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-emerald-400 hover:shadow-lg transition-all cursor-pointer"
                >
                  <PrinterIcon className="w-4 h-4" />
                  Print
                </button>
                <button 
                  onClick={() => setSelectedOrder(null)}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-all cursor-pointer"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Invoice Content */}
            <div className="p-8 md:p-12 space-y-8 bg-slate-900 print:bg-white print:p-0" id="invoice-content">
              {/* Branding (visible in print) */}
              <div className="hidden print:flex justify-between items-start border-b-2 border-slate-900 pb-8 mb-8 text-slate-950">
                <div>
                  <h1 className="text-2xl font-black tracking-tighter">TWIGGLE ORAL CARE</h1>
                  <p className="text-slate-500 text-xs mt-0.5">Premium Eco-Friendly Dental Solutions</p>
                </div>
                <div className="text-right">
                  <h2 className="text-lg font-bold text-slate-900 leading-none">INVOICE</h2>
                  <p className="text-slate-500 text-xs mt-1">#ORD-{selectedOrder.id}</p>
                  <p className="text-slate-500 text-xs">{new Date(selectedOrder.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-slate-300 print:text-slate-950">
                <div>
                  <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 print:text-slate-400">Customer Information</h4>
                  <div className="space-y-1">
                    <p className="font-bold text-white text-lg print:text-slate-950">{selectedOrder.first_name} {selectedOrder.last_name}</p>
                    <p className="text-slate-400 print:text-slate-600">{selectedOrder.email}</p>
                    <p className="text-slate-400 print:text-slate-600">{selectedOrder.phone}</p>
                  </div>
                </div>
                <div>
                  <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 print:text-slate-400">Shipping Address</h4>
                  <div className="space-y-1 text-slate-400 print:text-slate-600">
                    <p>{selectedOrder.address}</p>
                    <p>{selectedOrder.city}, {selectedOrder.province}</p>
                    <p>{selectedOrder.postal_code}</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 print:text-slate-400">Order Items</h4>
                <div className="border border-slate-800 rounded-xl overflow-hidden print:border-slate-300">
                  <table className="w-full text-left text-sm text-slate-300 print:text-slate-950">
                    <thead className="bg-slate-950 border-b border-slate-800 text-slate-500 text-[10px] font-black uppercase tracking-wider print:bg-slate-100 print:border-slate-300">
                      <tr>
                        <th className="px-6 py-3">Product</th>
                        <th className="px-6 py-3 text-center">Qty</th>
                        <th className="px-6 py-3 text-right">Price</th>
                        <th className="px-6 py-3 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-850 print:divide-slate-300">
                      {(() => {
                        try {
                          const items = typeof selectedOrder.cart_items === 'string' 
                            ? JSON.parse(selectedOrder.cart_items) 
                            : selectedOrder.cart_items;
                          if (!Array.isArray(items)) return null;
                          return items.map((item, i) => (
                            <React.Fragment key={i}>
                              <tr className="print:bg-white">
                                <td className="px-6 py-4">
                                  <div className="font-bold text-white print:text-slate-950">{item.name}</div>
                                  {item.subscription && (
                                    <div className="text-xs text-emerald-400 mt-1 font-medium print:text-emerald-700">Subscription: {item.subscription}</div>
                                  )}
                                </td>
                                <td className="px-6 py-4 text-center text-slate-400 print:text-slate-600">{item.quantity}</td>
                                <td className="px-6 py-4 text-right text-slate-400 print:text-slate-600">${parseFloat(item.pricePerBox || 0).toFixed(2)}</td>
                                <td className="px-6 py-4 text-right font-bold text-white print:text-slate-950">${parseFloat(item.lineTotal || 0).toFixed(2)}</td>
                              </tr>
                              {item.engravingImage && (
                                <tr className="bg-slate-950/60 print:bg-slate-50">
                                  <td colSpan="4" className="px-6 py-4">
                                    <div className="flex items-start gap-4">
                                      <div className="flex-shrink-0">
                                        <img 
                                          src={item.engravingImage} 
                                          alt="Custom Logo Engraving" 
                                          className="w-24 h-24 object-contain border border-slate-800 rounded-xl bg-slate-900 p-1 shadow print:border-slate-300 print:bg-white"
                                        />
                                      </div>
                                      <div>
                                        <p className="text-xs font-black text-amber-400 uppercase tracking-wider mb-1 print:text-amber-700">Custom Logo Engraving</p>
                                        <p className="text-xs text-slate-400 print:text-slate-600">{item.engravingFileName || 'Logo file uploaded'}</p>
                                        <button 
                                          onClick={() => {
                                            try {
                                              const link = document.createElement('a');
                                              link.href = item.engravingImage;
                                              link.download = item.engravingFileName || 'engraving_logo';
                                              document.body.appendChild(link);
                                              link.click();
                                              document.body.removeChild(link);
                                            } catch (err) {
                                              window.open(item.engravingImage, '_blank');
                                            }
                                          }}
                                          className="mt-3.5 inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-black uppercase tracking-wider rounded-lg transition-all shadow-md shadow-amber-500/10 cursor-pointer print:hidden"
                                        >
                                          ⬇ Download Design File
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ));
                        } catch (e) {
                          return <tr><td colSpan="4" className="px-6 py-4 text-center text-red-500 text-xs">Error loading items</td></tr>;
                        }
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end pt-4 text-slate-300 print:text-slate-950">
                <div className="w-full max-w-xs space-y-3">
                  <div className="flex justify-between text-slate-400 print:text-slate-600">
                    <span>Subtotal</span>
                    <span className="font-bold text-white print:text-slate-950">${parseFloat(selectedOrder.subtotal).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-400 print:text-slate-600">
                    <span>Shipping</span>
                    <span className="font-bold text-white print:text-slate-950">${parseFloat(selectedOrder.shipping_cost).toFixed(2)}</span>
                  </div>
                  <div className="border-t border-slate-800 pt-3 flex justify-between print:border-slate-300">
                    <span className="text-lg font-black text-white print:text-slate-950">Total</span>
                    <span className="text-2xl font-black text-emerald-400">${parseFloat(selectedOrder.total).toFixed(2)}</span>
                  </div>
                  <div className="pt-2 text-[10px] text-right text-slate-500 uppercase font-black tracking-wider">
                    Paid via Card ending in {selectedOrder.card_last4}
                  </div>
                </div>
              </div>

              {/* Footer (visible in print) */}
              <div className="hidden print:block border-t border-slate-300 pt-8 mt-12 text-center text-slate-500 text-xs italic">
                Thank you for choosing Twiggle Oral Care for your eco-friendly dental needs.
              </div>
            </div>
            
            {/* Global Print Styles */}
            <style>{`
              @media print {
                /* Reset HTML/Body for printing */
                html, body {
                  background: #ffffff !important;
                  color: #000000 !important;
                  height: auto !important;
                  overflow: visible !important;
                  margin: 0 !important;
                  padding: 0 !important;
                }

                @page {
                  size: portrait;
                  margin: 12mm 15mm 12mm 15mm !important;
                }
                
                /* Hide sidebar, header and main contents */
                aside, main, header, nav, button, .print\\:hidden, select {
                  display: none !important;
                }
                
                /* Position the modal wrapper cleanly */
                #invoice-modal-wrapper {
                  position: absolute !important;
                  left: 0 !important;
                  top: 0 !important;
                  width: 100% !important;
                  height: auto !important;
                  margin: 0 !important;
                  padding: 0 !important;
                  display: block !important;
                  background: #ffffff !important;
                  color: #000000 !important;
                  overflow: visible !important;
                  box-sizing: border-box !important;
                }

                /* Inner content container */
                #invoice-modal-container {
                  max-height: none !important;
                  height: auto !important;
                  width: 92% !important;
                  overflow: visible !important;
                  border: none !important;
                  box-shadow: none !important;
                  background: #ffffff !important;
                  color: #000000 !important;
                  margin: 0 auto !important;
                  padding: 0 !important;
                  box-sizing: border-box !important;
                }

                #invoice-content {
                  width: 100% !important;
                  background: #ffffff !important;
                  color: #000000 !important;
                  padding: 10mm 8mm !important;
                  margin: 0 !important;
                  overflow: visible !important;
                  box-sizing: border-box !important;
                }

                /* Text styling force black for laser printing */
                h1, h2, h3, h4, h5, h6, p, span, td, th, div {
                  color: #000000 !important;
                }

                /* Table styling for print */
                table {
                  width: 100% !important;
                  border-collapse: collapse !important;
                  box-sizing: border-box !important;
                }
                th, td {
                  border-bottom: 1px solid #cbd5e1 !important;
                  color: #000000 !important;
                  padding: 8px 12px !important;
                }

                /* Print color adjust */
                * {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
              }
            `}</style>
          </div>
        </div>
      )}

      {/* Bulk Quote Details Modal */}
      {selectedQuote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md">
          <div className="bg-slate-900 border border-slate-800 w-full max-w-2xl overflow-auto rounded-2xl shadow-2xl animate-in zoom-in-95 duration-300">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-800 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-black text-white">Bulk Quote Request Details</h3>
                <p className="text-xs text-slate-500">Ref ID: #QUO-{selectedQuote.id}</p>
              </div>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setSelectedQuote(null)}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-850 rounded-xl transition-all cursor-pointer"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-slate-300">
                <div>
                  <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Requester Info</h4>
                  <div className="space-y-1">
                    <p className="font-bold text-white text-base">{selectedQuote.name}</p>
                    <a href={`mailto:${selectedQuote.email}`} className="text-xs text-emerald-400 font-semibold hover:underline block">{selectedQuote.email}</a>
                  </div>
                </div>
                <div>
                  <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Organization</h4>
                  <div className="space-y-1">
                    <p className="font-bold text-white text-base">{selectedQuote.organization}</p>
                    <p className="text-xs text-slate-400">Preferred Quantity: <span className="text-emerald-400 font-bold">{selectedQuote.quantity} units</span></p>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-800/80 pt-6">
                <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Quote Requirements & Message:</h4>
                <div className="text-sm text-slate-300 bg-slate-950 p-5 rounded-xl border border-slate-850/60 leading-relaxed italic select-all">
                  "{selectedQuote.message || 'No additional message left.'}"
                </div>
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-slate-800/50">
                <a href={`mailto:${selectedQuote.email}?subject=Twiggle Bulk Quote Request - ${encodeURIComponent(selectedQuote.organization)}`} className="text-xs font-black text-emerald-400 hover:text-emerald-300 flex items-center gap-1 hover:underline cursor-pointer">
                  Reply via Email <EnvelopeIcon className="w-4 h-4 inline" />
                </a>
                <button 
                  onClick={() => setSelectedQuote(null)}
                  className="px-5 py-2.5 bg-slate-850 hover:bg-slate-800 text-slate-300 hover:text-white rounded-xl text-xs font-bold transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Product Create/Edit Modal */}
      {showProductModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 md:p-10 bg-slate-950/80 backdrop-blur-md overflow-y-auto">
          <div className="bg-slate-900 border border-slate-800 w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl animate-in zoom-in-95 duration-300 flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-900 sticky top-0 z-10">
              <div>
                <h3 className="text-lg font-black text-white">{selectedProduct ? 'Edit Product & Options' : 'Add New Product'}</h3>
                <p className="text-xs text-slate-500">Configure product specifications, images, and customizable client options.</p>
              </div>
              <button 
                onClick={() => setShowProductModal(false)}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-all cursor-pointer"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content Form */}
            <form onSubmit={handleSaveProduct} className="p-8 space-y-8 flex-1">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                
                {/* ─ Left: Core Product Info ─ */}
                <div className="space-y-5">
                  <h4 className="text-xs font-black text-emerald-400 uppercase tracking-widest pb-2 border-b border-slate-800">Core Specifications</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Product ID</label>
                      <input 
                        type="text" 
                        required
                        value={productForm.product_id}
                        onChange={e => setProductForm({...productForm, product_id: e.target.value})}
                        placeholder="e.g. twiggle-gold"
                        className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 text-sm focus:ring-1 focus:ring-emerald-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Slug</label>
                      <input 
                        type="text" 
                        required
                        value={productForm.slug}
                        onChange={e => setProductForm({...productForm, slug: e.target.value})}
                        placeholder="e.g. twiggle-gold"
                        className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 text-sm focus:ring-1 focus:ring-emerald-500 outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Short Name</label>
                    <input 
                      type="text" 
                      required
                      value={productForm.name}
                      onChange={e => setProductForm({...productForm, name: e.target.value})}
                      placeholder="e.g. Twiggle Gold"
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 text-sm focus:ring-1 focus:ring-emerald-500 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Full Marketing Name</label>
                    <input 
                      type="text" 
                      required
                      value={productForm.full_name}
                      onChange={e => setProductForm({...productForm, full_name: e.target.value})}
                      placeholder="e.g. Twiggle Gold — Luxury Bamboo Care"
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 text-sm focus:ring-1 focus:ring-emerald-500 outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Category</label>
                      <select 
                        value={productForm.category}
                        onChange={e => setProductForm({...productForm, category: e.target.value})}
                        className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 text-sm focus:ring-1 focus:ring-emerald-500 outline-none cursor-pointer"
                      >
                        <option value="adult">Adult</option>
                        <option value="kids">Kids</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Price Per Box ($)</label>
                      <input 
                        type="number" 
                        step="0.01"
                        required
                        value={productForm.price_per_box}
                        onChange={e => setProductForm({...productForm, price_per_box: parseFloat(e.target.value)})}
                        placeholder="169.00"
                        className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 text-sm focus:ring-1 focus:ring-emerald-500 outline-none"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Stock Status</label>
                      <select 
                        value={productForm.in_stock}
                        onChange={e => setProductForm({...productForm, in_stock: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 text-sm focus:ring-1 focus:ring-emerald-500 outline-none cursor-pointer"
                      >
                        <option value={1}>In Stock</option>
                        <option value={0}>Out of Stock</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Badge Text</label>
                      <input 
                        type="text" 
                        value={productForm.badge}
                        onChange={e => setProductForm({...productForm, badge: e.target.value})}
                        placeholder="144 Units / Box"
                        className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 text-sm focus:ring-1 focus:ring-emerald-500 outline-none"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Bristle Type</label>
                      <input 
                        type="text" 
                        value={productForm.bristle_type}
                        onChange={e => setProductForm({...productForm, bristle_type: e.target.value})}
                        placeholder="e.g. Ultra Soft"
                        className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 text-sm focus:ring-1 focus:ring-emerald-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Bristle Color</label>
                      <input 
                        type="text" 
                        value={productForm.bristle_color}
                        onChange={e => setProductForm({...productForm, bristle_color: e.target.value})}
                        placeholder="e.g. Gold"
                        className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 text-sm focus:ring-1 focus:ring-emerald-500 outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Short Card Description</label>
                    <textarea 
                      value={productForm.short_description}
                      onChange={e => setProductForm({...productForm, short_description: e.target.value})}
                      rows={2}
                      placeholder="Enter a brief teaser for shop cards..."
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 text-sm focus:ring-1 focus:ring-emerald-500 outline-none resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Main Page Description</label>
                    <textarea 
                      value={productForm.description}
                      onChange={e => setProductForm({...productForm, description: e.target.value})}
                      rows={3}
                      placeholder="Detailed rich description for product page..."
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 text-sm focus:ring-1 focus:ring-emerald-500 outline-none resize-none"
                    />
                  </div>
                </div>

                {/* ─ Right: Images, Features, Options CRUD ─ */}
                <div className="space-y-5">
                  <h4 className="text-xs font-black text-emerald-400 uppercase tracking-widest pb-2 border-b border-slate-800">Media, Features & Options</h4>

                  {/* Main Image Link & Upload */}
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Primary Card Image Path</label>
                    <div className="flex gap-2">
                      <input 
                        type="text" 
                        value={productForm.image}
                        onChange={e => setProductForm({...productForm, image: e.target.value})}
                        placeholder="e.g. /images/project/Gold brush.jpeg"
                        className="flex-1 px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 text-sm focus:ring-1 focus:ring-emerald-500 outline-none"
                      />
                      <label className="relative flex items-center justify-center px-4 py-2 bg-slate-850 hover:bg-slate-800 text-slate-200 text-xs font-bold rounded-xl border border-slate-700/60 cursor-pointer transition-all shrink-0 select-none">
                        {uploadingImage ? (
                          <div className="flex items-center gap-1.5">
                            <span className="w-3 h-3 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin"></span>
                            <span>Uploading...</span>
                          </div>
                        ) : (
                          <span>Upload File</span>
                        )}
                        <input 
                          type="file" 
                          accept="image/*" 
                          disabled={uploadingImage}
                          onChange={e => handleImageUpload(e.target.files[0], false)}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>

                  {/* Images Array Editor & Upload */}
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Product Gallery Images</label>
                    <div className="flex gap-2 mb-2">
                      <input 
                        type="text" 
                        value={newImageText}
                        onChange={e => setNewImageText(e.target.value)}
                        placeholder="Add gallery image URL path..."
                        className="flex-1 px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 text-sm outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (newImageText.trim()) {
                            setProductForm({...productForm, images: [...productForm.images, newImageText.trim()]})
                            setNewImageText('')
                          }
                        }}
                        className="px-3 bg-slate-850 hover:bg-slate-800 text-slate-200 text-xs font-bold rounded-xl border border-slate-700/60 transition-all shrink-0"
                      >
                        + Add
                      </button>
                      <label className="relative flex items-center justify-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 text-xs font-bold rounded-xl cursor-pointer transition-all shrink-0 select-none">
                        {uploadingGallery ? (
                          <div className="flex items-center gap-1.5">
                            <span className="w-3 h-3 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin"></span>
                            <span>Uploading...</span>
                          </div>
                        ) : (
                          <span>Upload</span>
                        )}
                        <input 
                          type="file" 
                          accept="image/*" 
                          disabled={uploadingGallery}
                          onChange={e => handleImageUpload(e.target.files[0], true)}
                          className="hidden"
                        />
                      </label>
                    </div>
                    <div className="max-h-20 overflow-y-auto space-y-1 bg-slate-950 p-2 rounded-xl border border-slate-850">
                      {productForm.images.map((img, i) => (
                        <div key={i} className="flex justify-between items-center text-xs text-slate-400">
                          <span className="truncate pr-4">{img}</span>
                          <button
                            type="button"
                            onClick={() => setProductForm({...productForm, images: productForm.images.filter((_, idx) => idx !== i)})}
                            className="text-red-400 font-bold hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      {productForm.images.length === 0 && <p className="text-[10px] text-slate-600">No gallery images added.</p>}
                    </div>
                  </div>

                  {/* Features List Array Editor */}
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Marketing Features List</label>
                    <div className="flex gap-2 mb-2">
                      <input 
                        type="text" 
                        value={newFeatureText}
                        onChange={e => setNewFeatureText(e.target.value)}
                        placeholder="Add dynamic feature point..."
                        className="flex-1 px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 text-sm outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (newFeatureText.trim()) {
                            setProductForm({...productForm, features: [...productForm.features, newFeatureText.trim()]})
                            setNewFeatureText('')
                          }
                        }}
                        className="px-3 bg-slate-850 hover:bg-slate-850 text-slate-200 text-xs font-bold rounded-xl transition-all"
                      >
                        + Add
                      </button>
                    </div>
                    <div className="max-h-28 overflow-y-auto space-y-1.5 bg-slate-950 p-3 rounded-xl border border-slate-850">
                      {productForm.features.map((feat, i) => (
                        <div key={i} className="flex justify-between items-start gap-2 text-xs text-slate-400">
                          <span className="leading-tight">• {feat}</span>
                          <button
                            type="button"
                            onClick={() => setProductForm({...productForm, features: productForm.features.filter((_, idx) => idx !== i)})}
                            className="text-red-400 font-bold hover:underline shrink-0"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      {productForm.features.length === 0 && <p className="text-[10px] text-slate-600">No feature points added.</p>}
                    </div>
                  </div>

                  {/* Option CRUD sub-panel */}
                  <div className="bg-slate-950/60 p-5 rounded-2xl border border-slate-800/80 space-y-4">
                    <div className="flex justify-between items-center border-b border-slate-800 pb-2">
                      <h5 className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Customizable Client Options</h5>
                      {selectedProduct ? (
                        <button
                          type="button"
                          onClick={() => setShowOptionForm(!showOptionForm)}
                          className="text-[10px] font-black text-emerald-400 hover:underline uppercase tracking-wider"
                        >
                          {showOptionForm ? 'Cancel' : '+ New Option'}
                        </button>
                      ) : (
                        <span className="text-[9px] text-slate-600 font-bold uppercase italic">Save product to edit options</span>
                      )}
                    </div>

                    {showOptionForm && selectedProduct && (
                      <div className="p-4 bg-slate-950 border border-slate-855 rounded-xl space-y-3 animate-in fade-in duration-300">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Option Name</label>
                            <input 
                              type="text" 
                              required
                              value={optionForm.name}
                              onChange={e => setOptionForm({...optionForm, name: e.target.value})}
                              placeholder="e.g. Bristle Softness"
                              className="w-full px-2 py-1.5 bg-slate-900 border border-slate-800 rounded-lg text-slate-200 text-xs outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Option Value</label>
                            <input 
                              type="text" 
                              required
                              value={optionForm.value}
                              onChange={e => setOptionForm({...optionForm, value: e.target.value})}
                              placeholder="e.g. Ultra Soft"
                              className="w-full px-2 py-1.5 bg-slate-900 border border-slate-800 rounded-lg text-slate-200 text-xs outline-none"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Price Modifier ($)</label>
                          <input 
                            type="number" 
                            step="0.01"
                            value={optionForm.price_modifier}
                            onChange={e => setOptionForm({...optionForm, price_modifier: parseFloat(e.target.value) || 0.00})}
                            placeholder="0.00"
                            className="w-full px-2 py-1.5 bg-slate-900 border border-slate-800 rounded-lg text-slate-200 text-xs outline-none"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleAddOption}
                          className="w-full py-1.5 bg-emerald-500 hover:bg-emerald-400 text-slate-955 text-xs font-bold rounded-lg transition-all"
                        >
                          Save New Option
                        </button>
                      </div>
                    )}

                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {selectedProduct && selectedProduct.options && selectedProduct.options.map(opt => (
                        <div key={opt.id} className="flex justify-between items-center p-2.5 bg-slate-950 border border-slate-855 rounded-xl">
                          <div className="min-w-0">
                            <span className="text-[10px] font-black text-emerald-400 uppercase tracking-wider block leading-tight">{opt.name}</span>
                            <span className="text-xs text-slate-300 font-bold">{opt.value}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-mono font-black text-slate-400">+${parseFloat(opt.price_modifier).toFixed(2)}</span>
                            <button
                              type="button"
                              onClick={() => handleDeleteOption(opt.id)}
                              className="text-red-400 hover:text-red-300 text-[10px] font-bold"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                      {selectedProduct && (!selectedProduct.options || selectedProduct.options.length === 0) && (
                        <p className="text-[10px] text-slate-600 italic">No options configured for this product yet.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Actions Footer */}
              <div className="border-t border-slate-800 pt-6 flex justify-end gap-3 bg-slate-900 sticky bottom-0 z-10 pb-2">
                <button
                  type="button"
                  onClick={() => setShowProductModal(false)}
                  className="px-5 py-2.5 bg-slate-850 hover:bg-slate-800 text-slate-300 hover:text-white rounded-xl text-xs font-bold transition-all"
                >
                  Close & Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-emerald-500 text-slate-950 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-emerald-400 hover:shadow-lg transition-all"
                >
                  Save Specifications
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

const NavItem = ({ active, to, icon, label, badge = 0, badgeColor = 'bg-slate-800 text-slate-400' }) => (
  <Link 
    to={to}
    className={`flex items-center justify-between w-full p-3 rounded-xl transition-all duration-300 ${
      active 
        ? 'bg-emerald-500 text-slate-950 font-black shadow-lg shadow-emerald-500/10 translate-x-1' 
        : 'text-slate-400 hover:text-white hover:bg-slate-850/50'
    }`}
  >
    <div className="flex items-center gap-3">
      {icon}
      <span className="tracking-tight text-sm">{label}</span>
    </div>
    {badge > 0 && (
      <span className={`px-2 py-0.5 text-[10px] font-black rounded-md ${active ? 'bg-slate-950 text-emerald-400' : badgeColor}`}>
        {badge}
      </span>
    )}
  </Link>
)

const StatCard = ({ label, value, icon, color }) => (
  <div className={`bg-gradient-to-br ${color} p-6 rounded-2xl border shadow-lg flex flex-col hover:-translate-y-1 hover:shadow-2xl transition-all duration-300`}>
    <div className="flex items-center justify-between mb-4">
      <div className="w-10 h-10 rounded-xl bg-slate-950/40 backdrop-blur-sm flex items-center justify-center border border-white/5 shadow-inner">
        {icon}
      </div>
      <span className="text-[9px] font-black uppercase tracking-wider text-slate-500">Live Data</span>
    </div>
    <div className="text-2xl font-black tracking-tight text-white">{value}</div>
    <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-1">{label}</div>
  </div>
)

export default AdminDashboard
